import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { fmtKRWFull } from "../../api/financeApi";

/**
 * 미수금 회수 전망 — 미수금을 만기일(dueDate) 기준으로 연체/이번주/이번달/다음달/그 이후로 나눠
 * 회수 예상액·건수를 보여준다. 연체(만기 지남)는 빨강으로 강조.
 * @param {Array} receivables getReceivables 응답 (clientName, receivableAmount, dueDate, status)
 */
export default function ReceivableOutlook({ receivables = [] }) {
    const buckets = useMemo(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const t = today.getTime();
        const in7 = t + 7 * 86400000;
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).getTime();
        const nextMonthEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0).getTime();

        const defs = [
            { key: "overdue", label: "연체 (만기 지남)", color: "#dc2626", bg: "#fef2f2", amount: 0, count: 0 },
            { key: "week",    label: "이번 주 만기",     color: "#d97706", bg: "#fffbeb", amount: 0, count: 0 },
            { key: "month",   label: "이번 달 만기",     color: "#2563eb", bg: "#eff6ff", amount: 0, count: 0 },
            { key: "next",    label: "다음 달 만기",     color: "#0f766e", bg: "#f0fdfa", amount: 0, count: 0 },
            { key: "later",   label: "그 이후",          color: "#64748b", bg: "#f8fafc", amount: 0, count: 0 },
        ];
        const idx = { overdue: 0, week: 1, month: 2, next: 3, later: 4 };

        (receivables || []).filter(r => r.status !== "PAID").forEach(r => {
            const amt = r.receivableAmount || 0;
            const due = r.dueDate ? new Date(r.dueDate).setHours(0, 0, 0, 0) : null;
            let k;
            if (due == null) k = "later";
            else if (due < t) k = "overdue";
            else if (due <= in7) k = "week";
            else if (due <= monthEnd) k = "month";
            else if (due <= nextMonthEnd) k = "next";
            else k = "later";
            defs[idx[k]].amount += amt;
            defs[idx[k]].count += 1;
        });
        return defs;
    }, [receivables]);

    const total = buckets.reduce((s, b) => s + b.amount, 0);
    const overdue = buckets[0];

    if (total === 0) {
        return <Typography sx={{ fontSize: 13, color: "#94a3b8", py: 2 }}>미수금이 없습니다.</Typography>;
    }

    return (
        <Box>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mb: 1.5 }}>
                회수 예정 미수금 <b style={{ color: "#0f766e" }}>{fmtKRWFull(total)}</b>
                {overdue.amount > 0 && <> · <b style={{ color: "#dc2626" }}>연체 {fmtKRWFull(overdue.amount)} ({overdue.count}건)</b></>}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {buckets.map(b => (
                    <Box key={b.key} sx={{
                        flex: 1, minWidth: 132, p: 1.5, borderRadius: 1.5, bgcolor: b.bg,
                        border: `1px solid ${b.color}22`,
                    }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: b.color }}>{b.label}</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: b.count ? b.color : "#cbd5e1", mt: 0.3, whiteSpace: "nowrap" }}>
                            {b.count ? fmtKRWFull(b.amount) : "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{b.count}건</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
